'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';

// UI lib components
import { AppShell, Box, LoadingOverlay } from '@mantine/core';

// Providers
import { ContextMenuProvider as MantineContextMenuProvider } from 'mantine-contextmenu';
import DialogProvider from '@features/Dialog/DialogProvider';
import AppSubscriptionProvider from '@app/shared/providers/AppSubscriptionProvider';

import TopBar from './TopBar';
import NavBar from './NavBar';
import UserOnboardingModal from '@features/User/UserOnboardingModal';
import ViewAsBanner from '@features/User/ViewAsBanner';
import useViewAs from '@shared/providers/ViewAsProvider/useViewAs';

// Context
import { WorkspaceLayoutProvider } from './context';
import { PageFileProvider } from '@features/Resource/PageFile/context';

// Local helpers & utils
import {
  canAccessWorkspace,
  isWorkspaceUserSuspended,
} from '@app/shared/helpers/auth';
import { useNavBar } from './store/navBarState';

// API Types
import {
  User,
  useGetMeQuery,
  useGetCurrentWorkspaceQuery,
  useCreateVisitActivityMutation,
} from '@app-types/blackroom-api';

// Style
import * as classes from './styles/index.css';
import * as appShellStylesAPI from './styles/appShellStylesAPI.css';
import { TOP_BAR_HEIGHT } from './TopBar/styles/variables';
import { RESOURCE_MENU_WIDTH } from '@app/features/Resource/ResourceMenu/styles/variables';
import { useHelpScout } from '@app/shared/hooks/useHelpScout';
import ResourceUploadMenu from '@features/Resource/ResourceUploadMenu';
import { isSpecialUserEmail } from '@shared/constants/users';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * Main logged-in layout
 */
function WorkspaceLayout() {
  const { data: meData, loading: loadingMe } = useGetMeQuery();
  const me = useMemo(() => meData?.me as User | undefined, [meData]);

  useHelpScout(import.meta.env.VITE_HELPSCOUT_BEACON_ID);

  /* ********************************** HOOKS ********************************* */

  // Store
  const navBar = useNavBar();
  const { isViewingAs } = useViewAs();

  // Routing
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useParams();

  // Slim workspace from GetMe — for routing/access checks only
  const slimWorkspace = useMemo(
    () =>
      me?.workspaceUsers.find((wu) => wu.workspace.id === workspaceId)
        ?.workspace,
    [me?.workspaceUsers, workspaceId],
  );

  // Detailed workspace from dedicated query (only for current workspace)
  const {
    data: workspaceData,
    loading: loadingWorkspace,
    error: workspaceError,
  } = useGetCurrentWorkspaceQuery({
    variables: { workspaceId: workspaceId! },
    skip: !workspaceId || !slimWorkspace,
  });

  // Keep last loaded workspace in a ref so the layout shell stays visible
  // during workspace switches (prevents full-page flash)
  const workspaceRef = useRef(workspaceData?.workspace);
  if (workspaceData?.workspace) {
    workspaceRef.current = workspaceData.workspace;
  }
  const workspace = workspaceData?.workspace ?? workspaceRef.current;

  const isPageFileLocation = useMemo(
    () => /(^|\/)file(\/|$)/.test(location.pathname),
    [location.pathname],
  );
  const hasNavBar = useMemo(
    () => /(^|\/)(file)(\/|$)/.test(location.pathname) === false,
    [location.pathname],
  );

  // State
  const [viewRedactedResource, setViewRedactedResource] =
    useState<boolean>(false);
  const [viewResourceInsights, setViewResourceInsights] =
    useState<boolean>(false);
  const [selectedFileText, setSelectedFileText] = useState<string>('');
  const [translatedText, setTranslatedText] = useState<string | undefined>('');
  const [hasAlreadyCreatedVisitActivity, setHasAlreadyCreatedVisitActivity] =
    useState<boolean>(false);
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);

  // API
  const [createVisitWorkspaceActivity] = useCreateVisitActivityMutation();

  /* ***************************** LOCAL VARIABLES **************************** */

  const user: Partial<User> | undefined = me;
  const isSpecialUser = isSpecialUserEmail(me?.email);

  const showNavBar = !isPageFileLocation;
  const showTopBar = !isPageFileLocation;

  /* ********************************* CALLBACKS ******************************** */

  /* ********************************** EFFECTS ********************************* */

  useEffect(() => {
    if (!me) return;

    // 1. User has no workspaces or tenant is suspended → go home
    if (me.workspaceUsers.length === 0 || me.managedTenant?.isSuspended) {
      navigate('/home');
      return;
    }

    // 2. No matching workspace in user's data (invalid ID or "landing")
    //    → redirect to first available workspace
    if (!slimWorkspace) {
      const firstWorkspace = me.workspaceUsers.find(
        (wu) => !!wu.workspace,
      )?.workspace;
      if (firstWorkspace) {
        navigate(`/workspace/${firstWorkspace.id}/resources/my`, {
          replace: true,
        });
      } else {
        navigate('/home');
      }
      return;
    }

    // 3. Workspace query still loading or stale ref → wait
    //    If the query errored (the API refuses suspended members and offline
    //    workspaces), redirect to the matching notice page.
    if (!workspace || workspace.id !== workspaceId) {
      if (workspaceError) {
        navigate(
          isWorkspaceUserSuspended(me, slimWorkspace)
            ? `/workspace/${slimWorkspace.id}/suspended`
            : `/workspace/${slimWorkspace.id}/offline`,
        );
      }
      return;
    }

    // 4. Check onboarding
    setShowOnboarding(!isViewingAs && (!me.firstName || !me.lastName));

    // 5. Access control checks
    if (isWorkspaceUserSuspended(me, workspace)) {
      navigate(`/workspace/${workspace.id}/suspended`);
    } else if (!canAccessWorkspace(me, workspace)) {
      navigate(`/workspace/${workspace.id}/offline`);
    }
  }, [me, slimWorkspace, workspace, workspaceId, workspaceError, isViewingAs]);

  useEffect(() => {
    if (!workspace) return;
    if (hasAlreadyCreatedVisitActivity) return;
    if (isViewingAs) return;
    if (isSpecialUser) return;

    createVisitWorkspaceActivity({
      variables: { workspaceId: workspace.id },
    });
    setHasAlreadyCreatedVisitActivity(true);
  }, [workspace, hasAlreadyCreatedVisitActivity, isViewingAs, isSpecialUser]);

  useEffect(() => {
    setHasAlreadyCreatedVisitActivity(false);
  }, [workspaceId]);

  /* ******************************** RENDERING ******************************* */

  if (
    loadingMe ||
    (!workspace && slimWorkspace && loadingWorkspace && !workspaceError)
  ) {
    return <LoadingOverlay overlayProps={{ blur: 3 }} visible />;
  }

  // Access refused: render the suspended/offline notice without the workspace shell
  // (other routes wait for the redirect above).
  if (
    user &&
    !workspace &&
    workspaceError &&
    /\/(suspended|offline)$/.test(location.pathname)
  ) {
    return (
      <WorkspaceLayoutProvider value={{ workspace, me }}>
        <Suspense>
          <Outlet />
        </Suspense>
      </WorkspaceLayoutProvider>
    );
  }

  if (!user || !workspace) {
    return null;
  }

  return (
    <Box p={0} className={navBar.className}>
      <ViewAsBanner />

      {/* Onboarding Modal */}
      {showOnboarding && me && slimWorkspace && (
        <UserOnboardingModal
          workspace={slimWorkspace}
          userId={me.id}
          onComplete={() => setShowOnboarding(false)}
        />
      )}

      <WorkspaceLayoutProvider
        value={{
          workspace,
          me,
        }}
      >
        <DialogProvider>
          <AppSubscriptionProvider>
            <PageFileProvider
              value={{
                viewRedactedFile: viewRedactedResource,
                toggleViewRedactedFile: (v) => {
                  setTranslatedText(undefined);
                  setViewResourceInsights(false);
                  setViewRedactedResource(v);
                },
                fileInsightsOpened: viewResourceInsights,
                toggleFileInsightsOpened: (v) => {
                  setTranslatedText(undefined);
                  setViewResourceInsights(v);
                },
                selectedText: selectedFileText,
                onSelectText: setSelectedFileText,
                translatedText,
                onTranslationReady: (v) => {
                  setViewResourceInsights(false);
                  setTranslatedText(v);
                },
                onResetTranslation: () => setTranslatedText(undefined),
              }}
            >
              <MantineContextMenuProvider>
                <AppShell
                  className={classes.workspaceLayout}
                  classNames={{ ...appShellStylesAPI }}
                  bg="blackroomWhite"
                  padding={0}
                  styles={{ root: { padding: 0 } }}
                  header={{ height: showTopBar ? TOP_BAR_HEIGHT : 0 }}
                  aside={{
                    width: RESOURCE_MENU_WIDTH,
                    breakpoint: 0,
                  }}
                  layout="alt"
                >
                  {showTopBar ? <TopBar user={user} /> : null}
                  {showNavBar ? <NavBar /> : null}

                  <AppShell.Main
                    className={classes.main}
                    style={{ paddingLeft: hasNavBar ? undefined : 0 }}
                  >
                    <Suspense>
                      <Outlet />
                    </Suspense>
                  </AppShell.Main>
                </AppShell>
              </MantineContextMenuProvider>
              <ResourceUploadMenu />
            </PageFileProvider>
          </AppSubscriptionProvider>
        </DialogProvider>
      </WorkspaceLayoutProvider>
    </Box>
  );
}

export default WorkspaceLayout;
